import React from 'react';

/**
 * PhotosBlock - Shared component for displaying teacher photos
 *
 * Used in both edit mode (preview) and public mode (full display).
 * Public mode includes photo click functionality for modal display.
 *
 * @param {Object} props
 * @param {Array} props.photos - All available photos
 * @param {Array} props.selectedPhotoIds - IDs of selected photos (for edit mode)
 * @param {Array} props.selectedPhotos - Direct selected photos array (for public mode)
 * @param {Function} props.onPhotoClick - Click handler for photo modal (public mode only)
 * @param {Object} props.translations - I18n translations object
 * @param {string} props.mode - Display mode: 'edit' (preview) or 'public' (full display)
 */
export default function PhotosBlock({
  photos,
  selectedPhotoIds = [],
  selectedPhotos,
  onPhotoClick,
  translations,
  mode = 'public'
}) {
  // Get selected photos
  const photosList = selectedPhotos || photos?.filter(p => selectedPhotoIds.includes(p.id)) || [];

  const getPhotoAlt = (index) => {
    const template = translations?.block_previews?.photo_alt || 'Photo %{index}';
    return template.replace('%{index}', index + 1);
  };

  // Edit mode: Show simple grid preview (max 4 photos)
  if (mode === 'edit') {
    const displayPhotos = photosList.slice(0, 4);
    const remainingCount = Math.max(0, photosList.length - 4);

    return (
      <>
        {photosList.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {displayPhotos.map((photo, index) => (
              <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={photo.url}
                  alt={photo.caption || getPhotoAlt(index)}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {remainingCount > 0 && (
              <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center">
                <span className="text-2xl font-semibold text-white">+{remainingCount}</span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-400 text-sm italic">
            {translations?.block_previews?.photos_placeholder || 'Click to edit and select photos to display'}
          </p>
        )}
      </>
    );
  }

  // Public mode: Show full responsive grid with hover effects and click handlers
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {photosList.map((photo, index) => (
        <div
          key={photo.id}
          className="relative group cursor-pointer"
          onClick={() => onPhotoClick && onPhotoClick(index)}
        >
          <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200">
            <img
              src={photo.thumb_url || photo.url}
              alt={`Teaching photo ${index + 1}`}
              className="w-full h-full object-cover group-hover:opacity-75 transition-opacity"
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-black/50 text-white rounded-full p-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
